import * as zod from 'zod';

import {
  BotHardware,
  HardwareConfigEquipmentMapping,
  HardwareConfigSchema,
} from '@sb/ai/types';
import { CartesianPose } from '@sb/geometry';
import { Step } from '@sb/remote-control/types';
import { Expression } from '@sb/routine-runner';

/**
 * Types matching the routine runner schema for "CreateDemonstration" operations.
 *
 * CreateDemonstration shares its recorder pipeline with CreateRecording (the
 * routine-runner class is a thin subclass that flips `recordingMode` to
 * `demonstration`), but is exposed as a separate step kind in the UI so demo
 * recordings don't drag in the user/task/project + cloud-skill upload flow.
 */
export namespace CreateDemonstrationStep {
  export const name = 'Create Demonstration';

  export const description =
    'Capture a demonstration of robot motion for skill training';

  export const isDecorator = true;

  export const librarySection = Step.LibrarySection.Hidden;

  export const librarySort = '7';

  export const argumentKind = 'CreateDemonstration';

  export const featureFlag = 'demonstrationRecording';

  // An expression tag with a display name for the UI
  export const TagExpression = zod.object({
    kind: zod.literal('TagExpression'),
    expression: Expression,
    displayName: zod.string(),
  });

  export type TagExpression = zod.infer<typeof TagExpression>;

  const TagValue = zod.union([zod.string().max(30), TagExpression]);

  export type TagValue = zod.infer<typeof TagValue>;

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    taskId: zod.string().nullable().default(null),
    hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
    userId: zod.string().nullable().default(null),
    projectId: zod.string().nullable().default(null),
    taskHardwareConfig: zod
      .object({
        id: zod.string(),
        name: zod.string(),
        config: HardwareConfigSchema,
      })
      .nullable()
      .default(null),
    botHardware: zod.record(zod.string(), BotHardware).default({}),
    timeout: zod.number().int().positive().default(300),
    tags: zod.array(TagValue).default([]),
    monitorSecondaryBotState: zod.boolean().default(false),
    // Optional reference frame: a space object / position-list entry /
    // Locate output the demonstration is anchored to. The runner resolves
    // its world pose at record start; replay places the demo relative to the
    // reference's current pose. Gated by `useReferenceFrame` so the operator
    // can turn anchoring off (and clear the picked reference) entirely.
    useReferenceFrame: zod.boolean().default(false),
    referenceRef: zod.string().nullable().default(null),
    // Which entry of a multi-entry reference position list to anchor to.
    // null ⇒ first entry. Ignored for single-pose references / Locate outputs.
    referenceIndex: zod.number().int().nonnegative().nullable().default(null),
    // Direct world-frame reference pose, already resolved by the dispatcher
    // (the wizard captures its Locate skill's live result at record start).
    // Takes precedence over `referenceRef` in the runner.
    referenceWorldPose: CartesianPose.nullable().default(null),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'CreateDemonstration',
      args: {
        taskId: args.taskId,
        hardwareConfigEquipmentMapping: args.hardwareConfigEquipmentMapping,
        userId: args.userId,
        projectId: args.projectId,
        taskHardwareConfig: args.taskHardwareConfig,
        botHardware: args.botHardware,
        timeout: args.timeout,
        tags: args.tags,
        // Demonstration recordings always upload to the demonstration_recordings
        // feathers table — there is no "save locally only" path for demos.
        uploadRecording: true,
        monitorSecondaryBotState: args.monitorSecondaryBotState,
        // Demonstrations are not part of the success/failure labeling flow.
        promptForLabel: false,
        // Pass the reference selection through only when anchoring is enabled;
        // the runner resolves its world pose (at the chosen list index) at
        // record start. `undefined` (not null) so the optional runner args
        // stay absent when anchoring is off or no reference is picked.
        referenceRef:
          args.useReferenceFrame && args.referenceRef
            ? args.referenceRef
            : undefined,
        referenceIndex:
          args.useReferenceFrame && args.referenceRef
            ? args.referenceIndex ?? undefined
            : undefined,
        referenceWorldPose: args.referenceWorldPose ?? undefined,
      },
    };
  };
}

CreateDemonstrationStep satisfies Step.StepKindInfo;
